import React, { FunctionComponent, useMemo, useState } from "react"
import { PromiseDispatch, useSelector } from "../../store"
import { useDispatch } from "react-redux"
import { UserState } from "../../store/user/types"
import { handleLogout } from "../../store/user/actions"
import { Link, useHistory, useRouteMatch } from "react-router-dom"
import PureImage from "../Image/PureImage"
import s from "./Navigation.less"
import Background from "../Background/Background"
import {
  Dropdown,
  DropdownContext,
  DropdownActivator,
  DropdownBody,
} from "../Dropdown"
import NavigationItem from "./NavigationItem"
import { useImageUrl } from "../../hooks/useImageUrl"
import { eImageVersions } from "../../definitions/Image"
import { iNavigation } from "../../api/NavigationMenus"
import { useNavigation } from "../../hooks/useNavigation"

const Navigation: FunctionComponent = () => {
  const { user } = useSelector(({ user }) => ({
    user,
  }))

  const dispatch = useDispatch<PromiseDispatch<void>>()
  const history = useHistory()
  const logOut = (): void => {
    dispatch(handleLogout()).then(() => {
      history.replace("/login")
    })
  }

  const title = user?.business?.title

  const handleUserName = (user: UserState) => {
    if (user.profile?.firstName && user.profile?.lastName) {
      return user.profile?.firstName + " " + user.profile?.lastName
    } else if (user?.emails?.[0]?.address) {
      return user?.emails[0]?.address
    }
  }
  const name = handleUserName(user || {})

  const cover = useImageUrl(
    eImageVersions.SWRegular,
    user.business?.coverObject,
  )
  const profileImage = useImageUrl(
    eImageVersions.LIcon,
    user.profile?.fileVersions,
  )

  return (
    <ul className={s.nav}>
      <li className={s.logo}>
        <Link to="/">
          <PureImage
            key={""}
            visibleByDefault={true}
            src={"/vraa-main-logo.png"}
          />
        </Link>
      </li>
      <Dropdown initiallyOpen={false}>
        <DropdownActivator>
          <li className={s.navProfile}>
            <Background className={s.background} key={cover} url={cover} />
            <div className={s.imageWrap}>
              <div className={s.image}>
                <PureImage
                  visibleByDefault={true}
                  src={profileImage}
                  brokenImage={"/img/avatar.png"}
                />
              </div>
              <DropdownContext.Consumer>
                {({ isOpen }) => (
                  <i
                    style={{ transform: `rotate(${isOpen ? 90 : 0}deg)` }}
                    className={`fa fa-chevron-right ${s.icon}`}
                  ></i>
                )}
              </DropdownContext.Consumer>
            </div>
            <div className={s.info}>
              {name && <span>{name}</span>}
              {title && <small>{title}</small>}
            </div>
          </li>
        </DropdownActivator>
        <DropdownBody>
          <ul>
            <NavigationItem>
              <Link to={"/profile"}>
                <i className="fa fa-user"></i>
                <span>My Profile</span>
              </Link>
            </NavigationItem>
            <NavigationItem>
              <Link to={"/business/profile"}>
                <i className="fa fa-building"></i>
                <span>Company Profile</span>
              </Link>
            </NavigationItem>
            <NavigationItem>
              <Link onClick={logOut} to={"#"}>
                <i className="fa fa-times-circle"></i>
                <span>Log out</span>
              </Link>
            </NavigationItem>
          </ul>
        </DropdownBody>
      </Dropdown>
      <div className={s.header}>
        <h3>Navigation</h3>
        <NavigationMenu />
      </div>
    </ul>
  )
}

export default Navigation

const NavigationMenu: FunctionComponent = () => {
  const navigation = useNavigation()

  return (
    <>
      {navigation?.map((menu, i) => (
        <DropdownItem key={i} menu={menu} />
      ))}
    </>
  )
}

interface iDropdownItemProps {
  menu: iNavigation
}

const DropdownItem: FunctionComponent<iDropdownItemProps> = ({ menu }) => {
  const match = useRouteMatch()
  const [isMatched, setIsMatched] = useState(false)

  useMemo(() => {
    if (menu.path === match.path) {
      setIsMatched(true)
      return
    } else if (menu.children) {
      let isActive = false
      menu.children.forEach(({ path }) => {
        if (match.path === path) isActive = true
      })
      if (isActive) {
        setIsMatched(true)
      } else {
        setIsMatched(false)
      }
    } else {
      setIsMatched(false)
    }
  }, [match.path])

  return (
    <Dropdown initiallyOpen={isMatched}>
      <DropdownContext.Consumer>
        {({ isOpen }) => (
          <DropdownActivator className={isOpen ? s.active : ""}>
            <NavigationItem>
              <Link to={"#"}>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <div>
                    <i className={menu.icon}></i>
                    <span>{menu.name}</span>
                  </div>
                  <div>
                    {menu.children && (
                      <i
                        style={{
                          transform: `rotate(${isOpen ? 90 : 0}deg)`,
                        }}
                        className={`fa fa-chevron-right ${s.icon}`}
                      ></i>
                    )}
                  </div>
                </div>
              </Link>
            </NavigationItem>
          </DropdownActivator>
        )}
      </DropdownContext.Consumer>
      <DropdownBody className={s.submenu}>
        {menu.children?.map((item, i) => (
          <SubItem key={i} menu={item} />
        ))}
      </DropdownBody>
    </Dropdown>
  )
}

const SubItem: FunctionComponent<iDropdownItemProps> = ({ menu }) => {
  const match = useRouteMatch()
  return useMemo(
    () => (
      <NavigationItem className={match.path === menu.path ? s.activeRoute : ""}>
        <Link to={menu.path}>
          <span>{menu.name}</span>
        </Link>
      </NavigationItem>
    ),
    [match.path],
  )
}
